import { Component, computed, effect, inject, input, untracked } from '@angular/core';
import { Store } from '@ngxs/store';
import { SecureLinkEntityType } from '@aiwyn/auth';
import { ClientRequestIdentityHelper } from '@aiwyn/common/client-requests';
import { CxpNewCrlPageComponent } from '../../../cxp/containers/cxp-new-crl-page/cxp-new-crl-page.component';
import { CrlItemStatus } from '../../../cxp/models';
import { CxpCrlDetailSelectors } from '../../../cxp/state/cxp-crl-detail-state.selectors';
import {
  HostedCrlPanelNavigator,
  provideHostedCrlPanelNavigator,
} from '../../../cxp/services/crl-panel-navigator';
import {
  OnSecureLinkEntityStatusUpdatedFn,
  SecureLinkClientRequest,
  SecureLinkDisplayAdaptor,
  SecureLinkEntityIdentity,
  SecureLinkEntityStatus,
} from '../../models';
import { ClientRequestWrapperComponent } from '../client-request-wrapper/client-request-wrapper.component';

/** Request-level statuses that count as done for the secure-link nav. */
const COMPLETED_STATUSES: ReadonlyArray<CrlItemStatus> = [
  CrlItemStatus.IN_REVIEW,
  CrlItemStatus.FINISHED,
];

/**
 * Renders the redesigned CRL inside the secure-link shell — the flag-gated
 * counterpart to {@link ClientRequestWrapperComponent}, selected by
 * `SecureLinkContainerComponent`'s display-component lookup.
 *
 * The redesigned page is normally routed; here it is hosted, so this component
 * supplies the request id and the panel navigator that stand in for the route.
 */
@Component({
  selector: 'app-crl-redesign-wrapper',
  standalone: true,
  imports: [CxpNewCrlPageComponent],
  providers: [provideHostedCrlPanelNavigator()],
  templateUrl: './crl-redesign-wrapper.component.html',
  styleUrl: './crl-redesign-wrapper.component.scss',
})
export class CrlRedesignWrapperComponent implements SecureLinkDisplayAdaptor<SecureLinkEntityType.CLIENT_REQUEST> {
  private readonly store = inject(Store);
  private readonly panelNavigator = inject(HostedCrlPanelNavigator);

  entity = input<SecureLinkClientRequest>(undefined);
  onStatusUpdated = input<OnSecureLinkEntityStatusUpdatedFn>(undefined);
  canShowCompletionDialog = input<boolean>(undefined);

  protected readonly requestId = computed(() => {
    const identity = this.entity()?.request?.identity;
    return identity ? ClientRequestIdentityHelper.parseRequestIdentity(identity).id : null;
  });

  private readonly display = this.store.selectSignal(CxpCrlDetailSelectors.getSelectedRequestDisplay);

  constructor() {
    effect(() => {
      const status = this.display()?.status;
      const entity = this.entity();
      if (status == null || !entity) return;

      untracked(() => {
        this.onStatusUpdated()?.(
          ClientRequestWrapperComponent.getEntityIdentity(entity),
          COMPLETED_STATUSES.includes(status) ? SecureLinkEntityStatus.COMPLETED : SecureLinkEntityStatus.IN_PROGRESS,
        );
      });
    });

    // The link's grants name the items the caller may see, and the backend
    // returns exactly those — so a single-item page is a link to that one item
    // and opens straight into its panel. Several means there is no one item to
    // open, and the list is the right landing view.
    effect(() => {
      const sections = this.display()?.displaySections ?? [];
      const items = sections.flatMap((section) => [...section.openItems, ...section.finishedItems]);
      if (items.length !== 1) return;

      untracked(() => {
        if (this.panelNavigator.selectedItemId() === null) {
          this.panelNavigator.openItem(items[0].id);
        }
      });
    });
  }

  static normalizeStatus(status: Parameters<typeof ClientRequestWrapperComponent.normalizeStatus>[0]): SecureLinkEntityStatus {
    return ClientRequestWrapperComponent.normalizeStatus(status);
  }

  static getEntityIdentity(entity: SecureLinkClientRequest): SecureLinkEntityIdentity<SecureLinkEntityType.CLIENT_REQUEST> {
    return ClientRequestWrapperComponent.getEntityIdentity(entity);
  }
}
